import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, X, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const METADATA_URL = '/images/projects/projects_metadata.json';

const Galerie = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allImages, setAllImages] = useState([]);
  const [leftImages, setLeftImages] = useState([]);
  const [rightImages, setRightImages] = useState([]);
  const [leftIdx, setLeftIdx] = useState(0);
  const [rightIdx, setRightIdx] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [paused, setPaused] = useState(false);
  const leftTimer = useRef(null);
  const rightTimer = useRef(null);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  useEffect(() => {
    fetch(METADATA_URL)
      .then(r => r.json())
      .then(data => {
        setAllImages(data);
        const left = data.filter((_, i) => i % 2 === 0);
        const right = data.filter((_, i) => i % 2 === 1);
        setLeftImages(left);
        setRightImages(right);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (paused || lightbox.open || leftImages.length === 0) return;
    leftTimer.current = setInterval(() => {
      setLeftIdx(prev => (prev + 1) % leftImages.length);
    }, 2500);
    return () => clearInterval(leftTimer.current);
  }, [paused, lightbox.open, leftImages.length]);

  useEffect(() => {
    if (paused || lightbox.open || rightImages.length === 0) return;
    rightTimer.current = setInterval(() => {
      setRightIdx(prev => (prev + 1) % rightImages.length);
    }, 3200);
    return () => clearInterval(rightTimer.current);
  }, [paused, lightbox.open, rightImages.length]);

  const openLightbox = useCallback((image) => {
    const idx = allImages.findIndex(img => img.id === image.id);
    setLightbox({ open: true, index: idx >= 0 ? idx : 0 });
  }, [allImages]);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
  }, []);

  const prevImage = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index - 1 + allImages.length) % allImages.length
    }));
  }, [allImages.length]);

  const nextImage = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index + 1) % allImages.length
    }));
  }, [allImages.length]);

  useEffect(() => {
    if (!lightbox.open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightbox.open, closeLightbox, prevImage, nextImage]);

  const CarouselPanel = ({ images, currentIdx, side }) => {
    if (images.length === 0) return null;
    const img = images[currentIdx];

    return (
      <div
        className="relative w-full overflow-hidden rounded-xl cursor-pointer group bg-slate-900"
        style={{ height: 'clamp(300px, 50vh, 550px)' }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onClick={() => openLightbox(img)}
        data-testid={`carousel-${side}`}
      >
        <img
          key={img.id}
          src={`/images/projects/${img.webp}`}
          alt={`Réalisation béton décoratif - ${img.category}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {img.category}
        </div>
        <div className="absolute bottom-4 right-4 text-white/70 text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {currentIdx + 1}/{images.length}
        </div>
      </div>
    );
  };

  const currentLightboxImage = lightbox.open ? allImages[lightbox.index] : null;

  return (
    <div className="min-h-screen bg-slate-950">
      <SEO
        title="Galerie Photos - Nos Réalisations en Béton Décoratif | DumaExpertBéton"
        description="Découvrez nos réalisations en béton imprimé, désactivé, balayé et ciré. Plus de 150 photos de projets réels en Vaucluse et PACA."
        url="https://duma-expert-beton.fr/galerie"
      />

      {/* Header */}
      <div className="bg-slate-950 border-b border-slate-800 px-4 sm:px-6 lg:px-8 py-6">
        <div className="container mx-auto">
          <button
            onClick={handleBack}
            data-testid="gallery-back-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 rounded-lg border border-slate-700 transition-all duration-200 mb-5 cursor-pointer text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium text-sm">{t('common.back')}</span>
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Camera className="w-7 h-7 text-amber-400" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Nos Réalisations
            </h1>
          </div>
          <p className="text-base text-slate-400 mt-2 max-w-2xl">
            {allImages.length} photos de projets réels en béton décoratif — Vaucluse, Gard, Drôme et PACA
          </p>
        </div>
      </div>

      {/* Dual Carousel Section */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="dual-carousel-grid"
        >
          <CarouselPanel images={leftImages} currentIdx={leftIdx} side="left" />
          <CarouselPanel images={rightImages} currentIdx={rightIdx} side="right" />
        </div>

        {/* Thumbnail strip */}
        <div className="mt-8 overflow-x-auto pb-4 scrollbar-thin" data-testid="thumbnail-strip">
          <div className="flex gap-2 min-w-max px-1">
            {allImages.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => openLightbox(img)}
                className="flex-shrink-0 rounded-md overflow-hidden border-2 border-transparent hover:border-amber-400 transition-all duration-200 focus:outline-none focus:border-amber-400"
                data-testid={`thumb-${img.id}`}
              >
                <img
                  src={`/images/projects/${img.webp}`}
                  alt={img.category}
                  loading="lazy"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Stats bar */}
        <div className="mt-8 flex flex-wrap gap-6 justify-center text-center" data-testid="gallery-stats">
          {['Béton Imprimé', 'Béton Désactivé', 'Béton Balayé', 'Béton Lissé', 'Béton Ciré'].map(cat => {
            const count = allImages.filter(img => img.category === cat || 
              (cat === 'Béton Balayé' && img.category === 'Béton Bâlé')).length;
            if (count === 0) return null;
            return (
              <div key={cat} className="px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-lg font-bold text-amber-400">{count}</p>
                <p className="text-xs text-slate-400">{cat}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && currentLightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          data-testid="lightbox-overlay"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            data-testid="lightbox-close-btn"
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 z-50 text-white/70 text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full">
            {lightbox.index + 1} / {allImages.length}
          </div>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            data-testid="lightbox-prev-btn"
            className="absolute left-2 sm:left-6 z-50 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`/images/projects/${currentLightboxImage.filename}`}
              alt={`Réalisation ${currentLightboxImage.category}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              data-testid="lightbox-image"
            />
          </div>

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            data-testid="lightbox-next-btn"
            className="absolute right-2 sm:right-6 z-50 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
          >
            <ChevronRight className="w-7 h-7" />
          </button>

          {/* Category badge */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 bg-black/50 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
            {currentLightboxImage.category}
          </div>
        </div>
      )}
    </div>
  );
};

export default Galerie;
